import React, {useState, useEffect} from 'react';
import {observer} from 'mobx-react';
import s from '../s';
import * as emitter from '../../global/emitter';
import AbTestStore, {ABTestKeys} from '../../global/store/ab-test-store';
import InfoIcon from './shared/info-icon';
import {screenResizeStore} from '../../global/store/screen-resize-store';
import {modal, Portal} from '../../util';
import {getGlassCollapseState, getGlassInfoIconPosition, getGlassInnerClassName} from './glass-collapse';
import GlassOptions from './ab-glass-options-panel';
import {isGlassOptionsVariant, logGlassOptionsExposure, offeredGlassIds} from './ab-glass-options';
import {glassSectionRendered, isAcrylicGlazing, isOfferedGlassTile, selectGlass} from '../selection/glass';
import GlassInfoModals from './glass-info-modals';
import {useOptionList} from './option-list/option-list';

declare var __fast_ship: boolean;
declare const window: any;

const Glass: React.FC = observer(() => {
    const [, setForceUpdate] = useState<number>(0);

    // Glass options test (ab-glass-options.ts): exposed where the glass section renders with more than one option, in both arms.
    const sectionShown = glassSectionRendered();
    const offered = sectionShown ? offeredGlassIds(s.prod) : [];
    const exposed = sectionShown && offered.length > 1;
    const variant = exposed && isGlassOptionsVariant();
    useEffect(() => {
        if (exposed) {
            logGlassOptionsExposure();
        }
    }, [exposed]);

    // Read during observer render so crossing 960px re-renders without a local resize listener.
    const {canCollapse, defaultExpanded} = getGlassCollapseState({
        width: screenResizeStore.width,
        isCaf: Boolean(s.ps.is_caf),
        startExpanded: variant,
    });

    // Authoritative panel visibility. Non-collapsible (non-CAF desktop) stays expanded.
    const [expanded, setExpanded] = useState(defaultExpanded);

    useEffect(() => {
        emitter.eventEmitter.subscribe(emitter.EventType.AB, 'ab', () => setForceUpdate(n => n + 1));

        return () => {
            emitter.eventEmitter.unsubscribe(emitter.EventType.AB, 'ab');
        };
    }, []);

    useEffect(() => {
        setExpanded(defaultExpanded);
    }, [defaultExpanded]);

    const save = selectGlass;
    const glassTiles = sectionShown && !variant ? s.glass.filter(isOfferedGlassTile) : [];
    const glassList = useOptionList(
        glassTiles.map(m => ({domId: `glass-option-${m.id}`, value: m.id})),
        s.ps.glass_id,
        save,
    );

    const toggleExpanded = () => {
        if (!canCollapse) return;
        setExpanded(prev => !prev);
    };

    if (!sectionShown) {
        return null;
    }

    const standardGlassTitle = (id: number = s.ps.glass_id) => id === 1 && AbTestStore.ABIsVariant(ABTestKeys.product_glass, 1) && 'Basic';

    const desktopHead = (
        <div className="cust-desktop-head">
            <span>Glass: </span>
            <span>
                {isAcrylicGlazing() ? 'Acrylic ' : ''}
                {standardGlassTitle() || s.ui.name_glass}
            </span>
        </div>
    );

    const mobileHead = (
        <div className="cust-flex-head">
            <div>
                <div
                    className="header-std flexer cust-flex2"
                    style={{paddingRight: '0.25rem'}}
                >
                    Glass
                </div>
                <div className="cust-flex2">{standardGlassTitle() || s.ui.name_glass}</div>
            </div>
        </div>
    );

    return (
        <div
            className="gray-box"
            style={{padding: 0}}
        >
            <div>
                <GlassInfoModals />

                <div>
                    <div
                        style={{
                            padding: '0 12px',
                            position: 'relative',
                        }}
                    >
                        {canCollapse ? (
                            <button
                                type="button"
                                className="d-none d-xl-block toggle-bs glass-toggle"
                                onClick={toggleExpanded}
                                aria-expanded={expanded}
                                aria-controls="glass-inner"
                            >
                                {desktopHead}
                            </button>
                        ) : (
                            <div className="d-none d-xl-block toggle-bs">{desktopHead}</div>
                        )}

                        {canCollapse ? (
                            <button
                                type="button"
                                className="d-block d-xl-none toggle-bs glass-toggle"
                                onClick={toggleExpanded}
                                aria-expanded={expanded}
                                aria-controls="glass-inner"
                            >
                                {mobileHead}
                            </button>
                        ) : (
                            <div className="d-block d-xl-none toggle-bs">{mobileHead}</div>
                        )}

                        {/* Info icon stays outside the disclosure button to avoid nested interactive elements. */}
                        <div style={getGlassInfoIconPosition({width: screenResizeStore.width, isCaf: Boolean(s.ps.is_caf)})}>
                            <InfoIcon
                                id="modal-glass"
                                ariaLabel="Open information about the glass upgrade options for this product"
                                useMargin={true}
                                styleBlack={true}
                            />
                        </div>
                    </div>
                    <div
                        id="glass-inner"
                        style={{
                            padding: '0 12px',
                        }}
                        className={getGlassInnerClassName(canCollapse, expanded)}
                    >
                        {variant ? (
                            <GlassOptions
                                offered={offered}
                                onSelect={save}
                            />
                        ) : (
                            <div
                                className="glass-prod"
                                role="listbox"
                                style={{paddingTop: 0}}
                                aria-label="Select frame glass"
                                onKeyDown={glassList.onKeyDown}
                            >
                                {glassTiles.map(m => {
                                    const opt = glassList.option(m.id);
                                    return (
                                        <div
                                            key={m.id}
                                            id={opt.id}
                                            title={standardGlassTitle(m.id) || m.title}
                                            aria-label={`Select ${standardGlassTitle(m.id) || m.title} glass`}
                                            aria-selected={opt.ariaSelected}
                                            className={`cust-entity${s.ps.glass_id === m.id ? ' active' : ''}`}
                                            onClick={opt.onActivate}
                                            onKeyDown={opt.onKeyDown}
                                            role="option"
                                            tabIndex={opt.tabIndex}
                                        >
                                            <div className="title">
                                                {standardGlassTitle(m.id) || m.title} {m.price}
                                                {s.__fast_ship_orig && s.isRetail && m.id === 1 && (
                                                    <div className={`prod-fast-shipping fast-shipping-${Portal?.PortalID}`}>
                                                        <i className={Portal?.PortalID !== 3 ? `fas fa-shipping-fast` : `fas fa-cart-shopping-fast`}></i>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div className="glass-info">
                            {isAcrylicGlazing() && (
                                <div className="mb-2">
                                    Why does this frame have acrylic?{' '}
                                    <InfoIcon
                                        id="modal-plexi-info"
                                        ariaLabel="Why does this frame have acrylic?"
                                        useMargin={true}
                                    />
                                </div>
                            )}
                            <div>
                                <button
                                    className="link link-btn"
                                    style={{fontSize: 16}}
                                    onClick={() => modal('modal-glass')}
                                    aria-label="Open information about glass options"
                                    type="button"
                                    aria-haspopup="dialog"
                                    aria-controls="modal-glass"
                                >
                                    Learn more
                                </button>{' '}
                                about our document protection options.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default Glass;
